import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";

import { createTallyLibrary } from "../../lib/TallyLibrary";
import {
  TallyAnimationProps,
  ParticleFunctionType,
} from "../../lib/TallyLibrary/types";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { mapRange } from "../../lib/TallyLibrary/helpers";

const WIDTH = 300;
const HEIGHT = 300;

const ConversationPanelAnimation = forwardRef(
  (
    {
      width,
      height,
      blobSpeed,
      particleAmount,
      scale,
      isResponsive,
      responsiveFactor,
    }: TallyAnimationProps,
    ref,
  ) => {
    let particles: ParticleFunctionType[] = [];
    const settings = {
      width: width || SETTINGS.width,
      height: height || SETTINGS.height,
      blobSpeed: blobSpeed || SETTINGS.blobSpeed,
      particleAmount: particleAmount || SETTINGS.particleAmount,
      scale: scale || SETTINGS.scale,
      isResponsive: isResponsive || SETTINGS.isResponsive,
      responsiveFactor: responsiveFactor || SETTINGS.responsiveFactor,
    };
    const refCanvas = useRef<HTMLCanvasElement>(null);
    const particleLibrary = useRef<ReturnType<
      typeof createTallyLibrary
    > | null>(null);
    let raf: number | null = null;

    useEffect(() => {
      if (refCanvas.current && !particleLibrary.current) {
        particleLibrary.current = createTallyLibrary({
          canvas: refCanvas.current,
          config: {
            dotColor: "off-white",
            includeMagnitation: true,
            blobStrength: 0,
          },
        });

        if (settings.isResponsive) {
          resizeHandler();
          window.addEventListener("resize", resizeHandler);
        }
      }
      drawParticles();

      return () => {
        if (settings.isResponsive) {
          window.removeEventListener("resize", resizeHandler);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      return () => {
        if (raf) {
          window.clearInterval(raf);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [particleLibrary.current]);

    useImperativeHandle(ref, () => ({
      start() {
        particleLibrary.current?.start();
      },
      stop() {
        particleLibrary.current?.stop();
      },
    }));

    const drawParticles = () => {
      if (particleLibrary.current) {
        particles = [];
        for (let i = 0; i < settings.particleAmount; i++) {
          const particle = particleLibrary.current.createParticle("px", i);
          particles.push(particle);
        }

        const context = refCanvas.current?.getContext("2d");
        let blobOffset = {
          x: 0,
          y: 0,
        };

        if (!refCanvas.current) return;
        const width = refCanvas.current.width;
        const height = refCanvas.current.height;

        const animate = () => {
          if (context && refCanvas.current) {
            blobOffset.x += settings.blobSpeed;
            blobOffset.y += settings.blobSpeed;
            context.clearRect(0, 0, width, height);

            context.save();

            const x = mapRange(1, 3, 0, width, settings.scale);
            const y = mapRange(1, 3, 0, height, settings.scale);
            context.translate(x, y);

            context.scale(settings.scale, settings.scale);

            particles.forEach((particle) => {
              particle.Move(blobOffset);
            });

            context.restore();
          }

          raf = requestAnimationFrame(animate);
        };

        animate();
      }
    };

    const resizeHandler = () => {
      if (refCanvas.current) {
        const width = WIDTH;
        const height = HEIGHT;
        refCanvas.current.style.width = width + "px";
        refCanvas.current.style.height = height + "px";
        particleLibrary.current?.update({ width, height });
      }
    };

    return (
      <canvas
        ref={refCanvas}
        className="tally-canvas-conversation"
        width={settings.width}
        height={settings.height}
      ></canvas>
    );
  },
);

export default ConversationPanelAnimation;
